import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faTimes, faCheck, faChevronDown, faChevronUp, faUpload } from '@fortawesome/free-solid-svg-icons';
import { ButtonComponent } from '../button/button.component';
import { TenantAdmin, CreateTenantPayload, UpdateTenantPayload } from '../../interfaces/tenant-admin.interface';
import { TenantColors } from '../../interfaces/tenant.interface';
import { TenantsAdminService } from '../../services/tenants-admin.service';
import { AlertService } from '../alert-service/alert-service';

const LIGHT_ALPHAS: Record<string, number> = { bgSelected: 0.08, buttonBg: 0.05, buttonHoverBg: 0.1, buttonBorder: 0.2 };
const DARK_ALPHAS: Record<string, number> = { bgSelected: 0.2, buttonBg: 0.15, buttonHoverBg: 0.25, buttonBorder: 0.3 };

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16) || 0,
    g: parseInt(clean.slice(2, 4), 16) || 0,
    b: parseInt(clean.slice(4, 6), 16) || 0
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function colorDistance(hexA: string, hexB: string): number {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

function lightenHex(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2 / 255;
  const d = (max - min) / 255;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / 255 / d) % 6;
    else if (max === g) h = (b - r) / 255 / d + 2;
    else h = (r - g) / 255 / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const newL = Math.min(1, l + amount / 100);
  const c = (1 - Math.abs(2 * newL - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = newL - c / 2;
  let [r2, g2, b2] = [0, 0, 0];
  if (h < 60) [r2, g2, b2] = [c, x, 0];
  else if (h < 120) [r2, g2, b2] = [x, c, 0];
  else if (h < 180) [r2, g2, b2] = [0, c, x];
  else if (h < 240) [r2, g2, b2] = [0, x, c];
  else if (h < 300) [r2, g2, b2] = [x, 0, c];
  else [r2, g2, b2] = [c, 0, x];
  return rgbToHex((r2 + m) * 255, (g2 + m) * 255, (b2 + m) * 255);
}

function extractPaletteFromImage(imageUrl: string, maxColors: number): Promise<string[]> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const size = 80;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve([]); return; }
      ctx.drawImage(img, 0, 0, size, size);

      let data: Uint8ClampedArray;
      try {
        data = ctx.getImageData(0, 0, size, size).data;
      } catch {
        resolve([]);
        return;
      }

      const buckets = new Map<string, { count: number; r: number; g: number; b: number }>();
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
        if (a < 200) continue;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        const lightness = (max + min) / 2;
        const saturation = max === min ? 0 : (max - min) / (255 - Math.abs(2 * lightness - 255));
        if (lightness > 235 || lightness < 20 || saturation < 0.15) continue;

        const key = `${Math.round(r / 24)}-${Math.round(g / 24)}-${Math.round(b / 24)}`;
        const bucket = buckets.get(key);
        if (bucket) bucket.count++;
        else buckets.set(key, { count: 1, r, g, b });
      }

      const sorted = Array.from(buckets.values()).sort((a, b) => b.count - a.count);
      const colors: string[] = [];
      for (const bucket of sorted) {
        const hex = rgbToHex(bucket.r, bucket.g, bucket.b);
        if (!colors.some(c => colorDistance(c, hex) < 40)) colors.push(hex);
        if (colors.length >= maxColors) break;
      }
      resolve(colors);
    };
    img.onerror = () => resolve([]);
    img.src = imageUrl;
  });
}

type ToneKey = 'bgSelected' | 'buttonBg' | 'buttonHoverBg' | 'buttonBorder';

function parseRgba(value: string): { r: number; g: number; b: number; a: number } {
  const match = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\)/);
  if (!match) return { r: 0, g: 0, b: 0, a: 1 };
  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10),
    a: match[4] !== undefined ? parseFloat(match[4]) : 1
  };
}

function buildRgba(r: number, g: number, b: number, a: number): string {
  return `rgba(${r}, ${g}, ${b}, ${Math.round(a * 100) / 100})`;
}

function computeDerivedColors(accentHex: string, dark: boolean): TenantColors {
  const { r, g, b } = hexToRgb(accentHex);
  const alphas = dark ? DARK_ALPHAS : LIGHT_ALPHAS;
  return {
    accentColor: accentHex,
    bgSelected: `rgba(${r}, ${g}, ${b}, ${alphas['bgSelected']})`,
    buttonBg: `rgba(${r}, ${g}, ${b}, ${alphas['buttonBg']})`,
    buttonHoverBg: `rgba(${r}, ${g}, ${b}, ${alphas['buttonHoverBg']})`,
    buttonBorder: `rgba(${r}, ${g}, ${b}, ${alphas['buttonBorder']})`
  };
}

@Component({
  selector: 'app-modal-create-edit-tenant',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule, ButtonComponent],
  templateUrl: './modal-create-edit-tenant.component.html',
  styleUrls: ['./modal-create-edit-tenant.component.scss']
})
export class ModalCreateEditTenantComponent implements OnChanges {
  @Input() show = false;
  @Input() isEdit = false;
  @Input() tenant: TenantAdmin | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() updated = new EventEmitter<void>();

  @ViewChild('logoSmallInput') logoSmallInput!: ElementRef<HTMLInputElement>;
  @ViewChild('logoFullInput') logoFullInput!: ElementRef<HTMLInputElement>;

  faTimes = faTimes;
  faCheck = faCheck;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faUpload = faUpload;

  loading = false;
  advancedMode = false;

  form = {
    slug: '',
    name: '',
    is_active: true
  };

  colors: TenantColors = computeDerivedColors('#FF009A', false);
  darkColors: TenantColors = computeDerivedColors('#FF009A', true);

  selectedLogoSmall: File | null = null;
  selectedLogoFull: File | null = null;
  logoSmallPreview: string | null = null;
  logoFullPreview: string | null = null;
  suggestedColors: string[] = [];

  constructor(
    private tenantsAdminService: TenantsAdminService,
    private translate: TranslateService,
    private alertService: AlertService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show'] && this.show) {
      this.resetForm();
    }
  }

  private resetForm(): void {
    this.advancedMode = false;
    this.selectedLogoSmall = null;
    this.selectedLogoFull = null;
    this.logoSmallPreview = this.tenant?.logoSmall || null;
    this.logoFullPreview = this.tenant?.logoFull || null;
    this.suggestedColors = [];

    if (this.isEdit && this.tenant) {
      this.form = {
        slug: this.tenant.slug,
        name: this.tenant.name,
        is_active: this.tenant.is_active
      };
      this.colors = { ...this.tenant.colors };
      this.darkColors = { ...this.tenant.darkColors };
    } else {
      this.form = { slug: '', name: '', is_active: true };
      this.colors = computeDerivedColors('#FF009A', false);
      this.darkColors = computeDerivedColors('#FF009A', true);
    }
  }

  onAccentChange(dark: boolean): void {
    if (this.advancedMode) return;
    if (dark) {
      this.darkColors = computeDerivedColors(this.darkColors.accentColor, true);
    } else {
      this.colors = computeDerivedColors(this.colors.accentColor, false);
    }
  }

  toggleAdvancedMode(): void {
    this.advancedMode = !this.advancedMode;
  }

  toneHex(value: string): string {
    const { r, g, b } = parseRgba(value);
    return rgbToHex(r, g, b);
  }

  toneAlphaPercent(value: string): number {
    return Math.round(parseRgba(value).a * 100);
  }

  updateToneColor(theme: TenantColors, key: ToneKey, hex: string): void {
    const { a } = parseRgba(theme[key]);
    const { r, g, b } = hexToRgb(hex);
    theme[key] = buildRgba(r, g, b, a);
  }

  updateToneAlpha(theme: TenantColors, key: ToneKey, alphaPercent: number): void {
    const { r, g, b } = parseRgba(theme[key]);
    theme[key] = buildRgba(r, g, b, alphaPercent / 100);
  }

  triggerLogoSmallInput(): void {
    this.logoSmallInput?.nativeElement?.click();
  }

  triggerLogoFullInput(): void {
    this.logoFullInput?.nativeElement?.click();
  }

  onLogoSmallSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.selectedLogoSmall = file;
    this.logoSmallPreview = URL.createObjectURL(file);
    this.suggestColorsFromFile(file);
  }

  onLogoFullSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.selectedLogoFull = file;
    this.logoFullPreview = URL.createObjectURL(file);
    if (!this.selectedLogoSmall) this.suggestColorsFromFile(file);
  }

  onLogoSmallError(): void {
    this.logoSmallPreview = null;
  }

  onLogoFullError(): void {
    this.logoFullPreview = null;
  }

  private suggestColorsFromFile(file: File): void {
    const url = URL.createObjectURL(file);
    extractPaletteFromImage(url, 5).then(colors => {
      this.suggestedColors = colors;
      URL.revokeObjectURL(url);
    });
  }

  applySuggestedColor(hex: string): void {
    this.colors = computeDerivedColors(hex, false);
    this.darkColors = computeDerivedColors(lightenHex(hex, 15), true);
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    if (!this.form.name.trim() || (!this.isEdit && !this.form.slug.trim() && this.form.slug !== '')) {
      return;
    }

    this.loading = true;

    if (this.isEdit && this.tenant) {
      const payload: UpdateTenantPayload = {
        name: this.form.name,
        colors: this.colors,
        darkColors: this.darkColors,
        is_active: this.form.is_active
      };
      this.tenantsAdminService.update(this.tenant.id, payload).subscribe({
        next: (tenant) => this.uploadLogosIfNeeded(tenant.id, 'BRANDING.UPDATE.SUCCESS'),
        error: (err) => {
          this.loading = false;
          this.alertService.showError(err);
        }
      });
    } else {
      const payload: CreateTenantPayload = {
        slug: this.form.slug.trim(),
        name: this.form.name,
        colors: this.colors,
        darkColors: this.darkColors,
        is_active: this.form.is_active
      };
      this.tenantsAdminService.create(payload).subscribe({
        next: (tenant) => this.uploadLogosIfNeeded(tenant.id, 'BRANDING.CREATE.SUCCESS'),
        error: (err) => {
          this.loading = false;
          this.alertService.showError(err);
        }
      });
    }
  }

  private uploadLogosIfNeeded(tenantId: string, successKey: string): void {
    const uploads: Promise<any>[] = [];
    if (this.selectedLogoSmall) {
      uploads.push(this.tenantsAdminService.uploadLogo(tenantId, this.selectedLogoSmall, 'small').toPromise());
    }
    if (this.selectedLogoFull) {
      uploads.push(this.tenantsAdminService.uploadLogo(tenantId, this.selectedLogoFull, 'full').toPromise());
    }

    Promise.all(uploads)
      .then(() => {
        this.loading = false;
        this.alertService.showSuccess(this.translate.instant(successKey));
        this.updated.emit();
        this.onClose();
      })
      .catch((err) => {
        this.loading = false;
        this.alertService.showError(err);
      });
  }
}
